import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    company: '', phone: '', address: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(
      formData.email, formData.password, formData.name,
      formData.company || null, formData.phone, formData.address
    );
    setLoading(false);

    if (result.success) {
      navigate('/customer/new-order');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-4" data-testid="back-to-home-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
        </Button>

        <div className="text-center mb-6">
          <img
            src="/at1s-logo-solid.png"
            alt="AirTech OneSolutions"
            className="h-24 w-auto mx-auto mb-2 object-contain"
          />
        </div>

        <Card className="shadow-lg border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-bold text-center">Create Account</CardTitle>
            <CardDescription className="text-center text-sm">Register to start ordering air filters</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" name="name" placeholder="John Smith" value={formData.name}
                    onChange={handleChange} required data-testid="register-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email}
                    onChange={handleChange} required data-testid="register-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name (optional)</Label>
                  <Input id="company" name="company" placeholder="ABC Manufacturing" value={formData.company}
                    onChange={handleChange} data-testid="register-company" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input id="phone" name="phone" type="tel" placeholder="(555) 123-4567" value={formData.phone}
                    onChange={handleChange} required data-testid="register-phone" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input id="address" name="address" placeholder="123 Main St, Houston, TX 77001" value={formData.address}
                  onChange={handleChange} required data-testid="register-address" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input id="password" name="password" type="password" placeholder="At least 6 characters" value={formData.password}
                    onChange={handleChange} required data-testid="register-password" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="Re-enter password" value={formData.confirmPassword}
                    onChange={handleChange} required data-testid="register-confirm-password" />
                </div>
              </div>

              <Button type="submit"
                className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 font-semibold"
                disabled={loading} data-testid="register-submit-btn">
                {loading ? 'Creating account...' : 'Create Account & Start Ordering'}
              </Button>
            </form>

            <div className="mt-5 text-center text-sm">
              <span className="text-gray-500">Already have an account? </span>
              <Link to="/login" className="text-sky-600 hover:text-sky-700 font-semibold" data-testid="login-link">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
